// Naming rules for area workspaces. Uniqueness has to live on the FE: the shared
// Workspace model delegates it to a hook Area Builder's detail model doesn't implement.

/** Letters the auto-name walks before it falls back to counting. */
const NAME_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Names are compared trimmed and case-insensitively, as the spec asks.
const normalize = (name: string): string => name.trim().toLowerCase();

// Whether an area already carries this name, ignoring case and surrounding space.
export const isWorkspaceNameTaken = (
  name: string,
  existing: string[]
): boolean => {
  const candidate = normalize(name);
  return existing.some((taken) => normalize(taken) === candidate);
};

// The name the Create Workspace modal opens on: the first unused "Workspace {letter}".
// Past Z it counts, so the field is never pre-filled with a name already taken.
export const nextWorkspaceName = (existing: string[]): string => {
  for (const letter of NAME_LETTERS) {
    const candidate = `Workspace ${letter}`;
    if (!isWorkspaceNameTaken(candidate, existing)) {
      return candidate;
    }
  }

  let ordinal = NAME_LETTERS.length + 1;
  while (isWorkspaceNameTaken(`Workspace ${ordinal}`, existing)) {
    ordinal += 1;
  }
  return `Workspace ${ordinal}`;
};

/** Why a typed name can't be used, or null when it can. */
export type WorkspaceNameProblem = 'required' | 'duplicate' | null;

// Validates a typed workspace name against the names already on the area.
export const validateWorkspaceName = (
  name: string,
  existing: string[]
): WorkspaceNameProblem => {
  if (name.trim() === '') {
    return 'required';
  }
  return isWorkspaceNameTaken(name, existing) ? 'duplicate' : null;
};
